import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  Accordion,
  BackLink,
  Button,
  Card,
  CheckIcon,
  DownloadIcon,
  Flex,
  StarIcon,
  Text,
  ToggleButtonGroup,
  ZdravstvenoIcon,
} from 'src/components';
import { colors } from 'src/theme';
import { formatNumber, i18n } from 'src/common';
import { CalculationResponseDto } from 'src/common/api/apollo/supplementary';
import { CoverageDetailsBox } from './parts/coverageDetailsBox.part';

/** Logo: only height set so frame fits content; width auto keeps aspect ratio. */
const LogoImg = styled.img({
  height: 30,
  width: 'auto',
  objectFit: 'contain',
  flexShrink: 0,
  display: 'block',
  verticalAlign: 'middle',
});

export interface MyChoiceOfferCardPackage {
  packageName: string;
}

export interface MyChoiceOfferCardProps {
  /** Products from getWebCalculationsSupplementary (birthDate). Package = productLabel. */
  products: CalculationResponseDto[];
  backPath: string;
  backState?: object;
  /** Called with the selected product when user clicks Dalje */
  onNext: (selectedProduct: CalculationResponseDto) => void;
  backButtonText: string;
  nextButtonText: string;
}

const PaketiSection = styled.div({
  background: colors.note3,
  borderRadius: 4,
  padding: 8,
  width: '100%',
});

const InfoSection = styled(Flex)({
  background: colors.background10,
  borderRadius: 8,
  padding: '8px 4px',
  width: '100%',
});

const SectionLabel = styled(Text)({
  textTransform: 'uppercase',
  letterSpacing: '1px',
});


/** Wrapper for each accordion item: background colors.background9. */
const AccordionWrapper = styled.div({
  background: colors.background9,
  borderRadius: 4,
  padding: 8,
  width: '100%',
});

/** Indents content to align with accordion title (after checkmark icon). Icon 16px + gap 8px */
const AccordionContentIndent = styled.div({
  paddingLeft: 20,
});

function splitIntoItems(description: string): string[] {
  if (!description?.trim()) return [];
  // Split only after a period that is NOT part of a number (e.g. "4." or "30.")
  const parts = description.trim().split(/(?<=[^\d]\.)\s+/).map((s) => s.trim()).filter(Boolean);
  return parts.length >= 1 ? parts : [description.trim()];
}

/** Uses offer from questionnaire step for header (price, label, logo). Rest hardcoded from Figma. Empty hyperlinks for "ovdje" and "web obrasca". */
export const MyChoiceOfferCard: React.FC<MyChoiceOfferCardProps> = ({
  products,
  backPath,
  onNext,
  backButtonText,
  nextButtonText,
}) => {

  const packages = products.map((p) => ({ packageName: p.label ?? '' })) ?? [];

  const [selectedPackage, setSelectedPackage] = useState<MyChoiceOfferCardPackage>(packages[packages.length - 1]);

  const handleTogglePackage = (value: string) => {
    const pkg = packages.find((p) => p.packageName === value);
    if (pkg) setSelectedPackage(pkg);
  };

  const product = products.find((p) => p.label === selectedPackage.packageName) ?? products[0];
  if (!product) return null;

  const base = 'components.cards.myChoiceOfferCard';
  const { logoUrl } = product;
  const yearlyPrice = product.prices.generalTransfer ?? 0;
  const monthlyPrice = (product.prices.generalTransferWithInstallments ?? 0) / 12;
  const offerSubtitle = product.label;
  const offerSubDescription = i18n.t(`${base}.offerSubDescription`);

  const coveragesIncluded: Array<{ title: string; items: string[] }> =
    product.fixedCoverages?.length
      ? product.fixedCoverages.map((item: any) => ({
        title: item!.label,
        items: item!.description ? splitIntoItems(item!.description) : [],
      }))
      : [
        { title: i18n.t(`${base}.coverage0Title`), items: [0, 1, 2, 3, 4, 5].map((i) => i18n.t(`${base}.coverage0Item${i}`)) },
        { title: i18n.t(`${base}.coverage1Title`), items: [i18n.t(`${base}.coverage1Item0`)] },
        { title: i18n.t(`${base}.coverage2Title`), items: [i18n.t(`${base}.coverage2Item0`)] },
        { title: i18n.t(`${base}.coverage3Title`), items: [i18n.t(`${base}.coverage3Item0`)] },
        { title: i18n.t(`${base}.coverage4Title`), items: [i18n.t(`${base}.coverage4Item0`)] },
        { title: i18n.t(`${base}.coverage5Title`), items: [i18n.t(`${base}.coverage5Item0`)] },
      ];

  const dodatakCoverages: Array<{ title: string; items: string[] }> =
    product.coverages?.length
      ? product.coverages.map((item: any) => ({
        title: item!.label,
        items: item!.description ? splitIntoItems(item!.description) : [],
      }))
      : [
        { title: i18n.t(`${base}.additionCoverage0Title`), items: [i18n.t(`${base}.additionCoverage0Item0`)] },
      ];

  const policyDescription = i18n.t(`${base}.policyDescription`);

  interface InfoTextProps {
    baseKey: string;
  }

  const InfoText: React.FC<InfoTextProps> = ({ baseKey }) => (
    <Flex vertical gap={4} style={{ width: '100%' }}>
      <Text type="small" color={colors.text1}>
        {i18n.t(`${baseKey}.infoLine1`)}
      </Text>
      <Text type="small" color={colors.text1}>
        {i18n.t(`${baseKey}.infoLine2`)}
      </Text>
      <Text type="small" bold color={colors.text1}>
        {i18n.t(`${baseKey}.infoLine3`)}
      </Text>
      <Flex vertical style={{ paddingTop: 16 }}>
        <Text type="small" color={colors.text1}>
          {i18n.t(`${baseKey}.infoLine4`)}
        </Text>
      </Flex>
      <Flex vertical style={{ width: '100%' }}>
        <div style={{ fontSize: 14, lineHeight: '20px', color: colors.text1 }}>
          {i18n.t(`${baseKey}.infoLine5`)}{' '}
          <Button
            btnType="textPrimary"
            size="small"
            text={`${i18n.t(`${baseKey}.infoLinkOvdje`)}.`}
            onClick={() => { }}
            style={{ padding: 0, minHeight: 'unset', display: 'inline-flex', verticalAlign: 'baseline' }}
          />
        </div>
      </Flex>
      <Flex vertical style={{ paddingTop: 16 }}>
        <Text type="small" color={colors.text1}>
          {i18n.t(`${baseKey}.infoLine6`)}
        </Text>
      </Flex>
      {/* div block container so sentence + button wrap as one line of text; Flex can't do inline flow */}
      <Flex vertical style={{ width: '100%' }}>
        <div style={{ fontSize: 14, lineHeight: '20px', color: colors.text1 }}>
          {i18n.t(`${baseKey}.infoLine7`)}{' '}
          <Button
            btnType="textPrimary"
            size="small"
            text={i18n.t(`${baseKey}.infoLinkWebObrasca`)}
            onClick={() => { }}
            style={{ padding: 0, minHeight: 'unset', display: 'inline-flex', verticalAlign: 'baseline' }}
          />
        </div>
      </Flex>
      <Text type="small" color={colors.text1}>
        {i18n.t(`${baseKey}.infoLine8`)}
      </Text>
    </Flex>
  );

  return (
    <Card radius={8} padding="0 8px 16px 8px" background={colors.white} style={{ width: '100%' }}>
      <Flex vertical gap={24} style={{ width: '100%' }}>
        {/* Brand and Price Section - tight spacing around logo */}
        <Flex vertical gap={4} style={{ padding: '8px 4px', paddingBottom: 0 }}>
          <Flex justify="flex-start" align="center" style={{ lineHeight: 0 }}>
            {logoUrl ? (
              <LogoImg src={logoUrl} alt="" />
            ) : (
              <ZdravstvenoIcon height={40} color={colors.primary} />
            )}
          </Flex>
          <Flex justify="space-between" align="center">
            <Flex vertical gap={2}>
              <Text type="base" color={colors.text1}>
                {offerSubtitle}
              </Text>
              <Text type="base" color={colors.text1}>
                {offerSubDescription}
              </Text>
            </Flex>
            <Flex vertical gap={2} align="flex-end">
              <Text type="small" color={colors.text1}>
                {formatNumber(monthlyPrice, '€')}/12mj
              </Text>
              <Text type="base" bold color={colors.text1}>
                {formatNumber(yearlyPrice, '€')}/god
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {/* Paketi Section */}
        <PaketiSection>
          <Flex vertical gap={8}>
            <ToggleButtonGroup
              label={i18n.t(`${base}.paketi`)}
              options={packages.map((pkg, i) => ({
                value: pkg.packageName,
                label: pkg.packageName,
                icon: i === packages.length - 1 ? <StarIcon height={16} width={16} /> : undefined,
              }))}
              value={selectedPackage.packageName}
              onChange={handleTogglePackage}
            />
            <Text type="small" color={colors.text1}>
              {policyDescription}
            </Text>
          </Flex>
        </PaketiSection>

        <Flex vertical gap={4}>
          <SectionLabel type="small" bold color={colors.text4}>
            {i18n.t(`${base}.includedInPrice`)}
          </SectionLabel>
          <Flex vertical gap={4}>
            {coveragesIncluded.map((coverage, index) => (
              <AccordionWrapper key={index}>
                <Accordion
                  title={
                    <Flex gap={8} align="center" justify="space-between" style={{ width: '100%' }}>
                      <Text type="small" bold color={colors.text1}>
                        {coverage.title}
                      </Text>
                    </Flex>
                  }
                  content={
                    <AccordionContentIndent>
                      {/* For multiple specific coverages with bullets use: showBullets={[0, 2, 4].includes(index)} */}
                      <CoverageDetailsBox title={coverage.title} items={coverage.items} hideTitle showBullets={coverage.items.length > 1} />
                    </AccordionContentIndent>
                  }
                  icon={<CheckIcon color={colors.success} width={20} height={20} />}
                  open={true}
                  expandable={false}
                  expandIcon={false}
                  gap={4}
                />
              </AccordionWrapper>
            ))}
          </Flex>
        </Flex>

        {/* Dodatak (Dijagnosticirane/prijavljene bolesti) */}
        <Flex vertical gap={4}>
          <SectionLabel type="small" bold color={colors.text4}>
            {i18n.t(`${base}.additionTitle`)}
          </SectionLabel>
          <Flex vertical gap={4}>
            {dodatakCoverages.map((coverage, index) => (
              <AccordionWrapper key={index}>
                <Accordion
                  title={
                    <Flex gap={8} align="center" justify="space-between" style={{ width: '100%' }}>
                      <Text type="small" bold color={colors.text1}>
                        {coverage.title}
                      </Text>
                    </Flex>
                  }
                  content={
                    <AccordionContentIndent>
                      <CoverageDetailsBox title={coverage.title} items={coverage.items} hideTitle showBullets={coverage.items.length > 1} />
                    </AccordionContentIndent>
                  }
                  icon={<CheckIcon color={colors.success} width={20} height={20} />}
                  open={true}
                  expandable={false}
                  expandIcon={false}
                  gap={4}
                />
              </AccordionWrapper>
            ))}
          </Flex>
        </Flex>

        {/* Info text with hyperlinks */}
        <InfoSection vertical>
          <InfoText baseKey={base} />
        </InfoSection>

        {/* Uvjeti osiguranja link - empty hyperlink per requirements */}
        <Flex align="center" gap={8} style={{ padding: 8 }}>
          <DownloadIcon width={14} height={14} color={colors.primary} />
          <Button
            btnType="textPrimary"
            size="small"
            text={i18n.t(`${base}.insuranceTerms`)}
            onClick={() => { }}
            style={{ padding: 0, minHeight: 'unset', fontWeight: 900, fontSize: 14, lineHeight: '20px' }}
          />
        </Flex>

        {/* Nazad / Dalje buttons */}
        <Flex justify="space-between" align="center" gap={8} style={{ padding: '8px 16px 0' }}>
          <BackLink text={backButtonText} path={backPath} />
          <Button
            btnType="primary"
            size="large"
            text={nextButtonText}
            onClick={() => onNext(product)}
            style={{ minWidth: 140 }}
          />
        </Flex>
      </Flex>
    </Card>
  );
};
